/**
 * CardinalityWarning Component
 *
 * Displays an inline warning when a linked table query would produce too many
 * intermediate rows. Shows estimated row count, threshold, and actionable
 * suggestions to fix the issue.
 */

import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Database, Lightbulb } from 'lucide-react';
import type { LinkedTableCardinalityError } from '@loader/shared-types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@loader/ui';

interface CardinalityWarningProps {
  error: LinkedTableCardinalityError;
}

function formatNumber(num: number): string {
  return num.toLocaleString();
}

function formatLargeNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(1)}B`;
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return formatNumber(num);
}

export function CardinalityWarning({ error }: CardinalityWarningProps) {
  const [showDetails, setShowDetails] = useState(false);

  const { estimatedRows, threshold, tableRowCounts, suggestions } = error.details;

  return (
    <div className="flex h-full flex-col items-center justify-center p-6">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4" />
            Query Configuration Issue
          </CardTitle>
          <CardDescription>
            This query would produce approximately{' '}
            <span className="font-semibold text-foreground">{formatLargeNumber(estimatedRows)} rows</span>,
            exceeding the {formatNumber(threshold)} row limit.
          </CardDescription>
        </CardHeader>

        <CardContent className="border-t pt-4 space-y-4">
          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div>
              <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                <Lightbulb className="h-4 w-4" />
                How to fix this
              </div>
              <ul className="text-muted-foreground space-y-1.5 text-sm">
                {suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="bg-muted-foreground mt-2 h-1 w-1 shrink-0 rounded-full" />
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Details Collapsible */}
          <Collapsible open={showDetails} onOpenChange={setShowDetails}>
            <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs">
              {showDetails ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {showDetails ? 'Hide details' : 'Show details'}
            </CollapsibleTrigger>

            <CollapsibleContent className="mt-3 space-y-3">
              <div>
                <div className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs font-medium">
                  <Database className="h-3 w-3" />
                  Table Row Counts
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(tableRowCounts).map(([alias, count]) => (
                    <div
                      key={alias}
                      className="bg-muted flex items-center justify-between rounded px-2 py-1"
                    >
                      <span className="font-mono">{alias}</span>
                      <span className="text-muted-foreground">{formatNumber(count)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-muted-foreground text-xs">
                Multiple LEFT JOINs without aggregation can create a Cartesian product,
                multiplying row counts across all joined tables.
              </p>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>
    </div>
  );
}
